import React from "react";

import { Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import { Input, IntegerPriceInput } from "components/Form/FormikControls";
import { FormikAwareBusinessPICSelector } from "components/Form/FormikControls/BusinessPICSelector";
import { Column } from "components/Layout";
import {
  ReviewCard,
  ReviewField,
  ReviewFieldGrid,
} from "components/ReviewCard";

import { BUYER } from "constants/businesses";
import { SaleType } from "constants/sale";
import { PricingType } from "components/SellingSaleLotForm/PricingType";
import { ForPaddock } from "containers/ForSaleType";
import { useHasBuyerSplitPermission, useToggle } from "hooks";
import { withNamespace } from "components/Form/FormikControls/lib";
import { FormCollapse } from "components/Form";
import { SubtleBadge } from "components/Badge";
import { MobileTransactionalCreateSaleFormValues } from "../lib/types";
import { useSaleLotAttributeLabels } from "../lib/useSaleLotAttributeLabels";

interface PriceTabProps {
  saleLotNamespace: string;
  saleType?: SaleType | null;
  deploymentId: number;
  speciesId?: number | null;
}

export const PriceTab = ({
  saleLotNamespace,
  saleType = null,
  deploymentId,
  speciesId = null,
}: PriceTabProps) => {
  const formikProps =
    useFormikContext<MobileTransactionalCreateSaleFormValues>();
  const {
    values: { saleLot },
  } = formikProps;

  const { ageLabel, breedLabel, sexLabel } = useSaleLotAttributeLabels(
    saleLot,
    deploymentId,
    speciesId,
  );

  const hasBuyerSplitPermission = useHasBuyerSplitPermission();

  const [showMorePricingFields, toggleShowMorePricingFields] = useToggle(false);

  return (
    <>
      <ReviewCard>
        <ReviewFieldGrid>
          <ReviewField xs={6} label="Head">
            {saleLot.quantity || "-"}
          </ReviewField>
          {sexLabel && (
            <ReviewField xs={6} label="Sex">
              {sexLabel}
            </ReviewField>
          )}
          {ageLabel && (
            <ReviewField xs={6} label="Age">
              {ageLabel}
            </ReviewField>
          )}
          {breedLabel && (
            <ReviewField xs={6} label="Breed">
              {breedLabel}
            </ReviewField>
          )}
        </ReviewFieldGrid>
      </ReviewCard>
      <Column gap={8}>
        <Grid item xs={12}>
          <PricingType
            namespace={saleLotNamespace}
            quantity={saleLot.quantity}
            readOnly={false}
            selectedSaleType={saleType}
            hideFields={{ quantity_delivered: true }}
          />
        </Grid>
      </Column>
      <FormCollapse
        isOpen={showMorePricingFields}
        onToggle={toggleShowMorePricingFields}
        header={<SubtleBadge>More</SubtleBadge>}
      >
        <>
          <ForPaddock selectedSaleType={saleType}>
            <Grid item xs={12}>
              <IntegerPriceInput
                label="Reserve Price"
                name={withNamespace(saleLotNamespace, "reservePrice")}
              />
            </Grid>
          </ForPaddock>
          <Grid xs={12} sm={4} item>
            <Input
              label="Hd Delivered"
              name={withNamespace(saleLotNamespace, "quantity_delivered")}
              type="number"
            />
          </Grid>
          <Grid item xs={12}>
            <FormikAwareBusinessPICSelector
              label="Invoice To"
              businessFieldName={withNamespace(
                saleLotNamespace,
                "invoiceToBusinessId",
              )}
              formikProps={formikProps}
              businessRoles={[BUYER]}
              optional
              clearable
              tooltip={
                hasBuyerSplitPermission
                  ? "The business that receives the invoice for this Sale Lot.  This will" +
                    " be overridden by any Buyer Splits."
                  : ""
              }
            />
          </Grid>
        </>
      </FormCollapse>
    </>
  );
};
